import { useEffect, useState } from "react";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { NavLink } from "@/components/NavLink";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Calendar, TrendingUp, LogOut, User, Settings, Menu } from "lucide-react";
import logo from "@/assets/logo.svg";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AvatarUpload } from "@/components/AvatarUpload";

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userId, setUserId] = useState<string>("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate("/auth");
    } else {
      setUserId(user.id);
      fetchProfile(user.id);
    }
  };

  const fetchProfile = async (userId: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("avatar_url")
      .eq("id", userId)
      .single();
    
    if (data) {
      setAvatarUrl(data.avatar_url);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-accent/20">
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 md:gap-3">
              <img src={logo} alt="Vocalzing logo" className="h-8 w-8 md:h-10 md:w-10" />
              <h1 className="text-lg md:text-2xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                Vocalzing
              </h1>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4">
              <nav className="flex gap-2">
                <NavLink to="/booking">
                  <Button
                    variant="ghost"
                    className={location.pathname === "/booking" ? "bg-accent" : ""}
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Book Lesson
                  </Button>
                </NavLink>
                <NavLink to="/progress">
                  <Button
                    variant="ghost"
                    className={location.pathname === "/progress" ? "bg-accent" : ""}
                  >
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Progress
                  </Button>
                </NavLink>
              </nav>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <Avatar>
                      <AvatarImage src={avatarUrl || undefined} alt="Profile" />
                      <AvatarFallback>
                        <User className="w-5 h-5" />
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80" align="end">
                  <div className="space-y-4">
                    <h4 className="font-medium text-center">Quick Actions</h4>
                    <AvatarUpload
                      currentAvatarUrl={avatarUrl}
                      userId={userId}
                      onAvatarUpdate={(url) => setAvatarUrl(url)}
                    />
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => navigate("/profile")}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Manage Profile
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
              <Button onClick={handleSignOut} variant="outline" size="sm">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </Button>
            </div>

            {/* Mobile Hamburger Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="sm">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px]">
                <div className="flex flex-col gap-4 mt-8">
                  <Button 
                    variant="ghost" 
                    onClick={() => {
                      navigate("/booking");
                      setMobileMenuOpen(false);
                    }}
                    className="w-full justify-start"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Book Lesson
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={() => {
                      navigate("/progress");
                      setMobileMenuOpen(false);
                    }}
                    className="w-full justify-start"
                  >
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Progress
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      navigate("/profile");
                      setMobileMenuOpen(false);
                    }}
                    className="w-full justify-start"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Manage Profile
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      handleSignOut();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full justify-start"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main>
        <Outlet />
      </main>
    </div>
  );
};

export default CustomerDashboard;
